import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { ClientFaqViewer } from '../components/ClientFaqViewer.jsx';
import { ProjectMediaLibrary } from '../components/ProjectMediaLibrary.jsx';
import { ProjectFileUploader } from '../components/ProjectFileUploader.jsx';
import { IntegrationWidgetsPanel } from '../components/IntegrationWidgetsPanel.jsx';
import { FigmaApprovalClientPanel } from '../components/FigmaApprovalClientPanel.jsx';
import { MailboxEmailsList, MailboxMeetingsList } from '../components/MailboxPanels.jsx';
import { MailMessageDialog } from '../components/MailClient.jsx';
import { SharePointFileBrowser } from '../components/SharePointFileBrowser.jsx';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import { WorkspaceTabs } from '../components/WorkspaceTabs.jsx';
import { ProjectHero } from '../components/ProjectHero.jsx';
import { ProjectOverviewBoard } from '../components/ProjectOverviewBoard.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { projectHealth } from '../utils/statusUi.js';
import { summarizeTaskProgress } from '../utils/taskProgress.js';
import { TaskBoard } from '../components/TaskBoard.jsx';

const PRIMARY_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'files-media', label: 'Files & Media' },
  { id: 'communication', label: 'Communication' },
  { id: 'forms', label: 'Forms' },
  { id: 'contacts', label: 'Contacts' },
];

const OVERFLOW_TABS = [
  { id: 'notes', label: 'Notes' },
  { id: 'tools', label: 'Tools & Links' },
  { id: 'activity', label: 'Activity' },
];

export function MyProjectDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [openMailId, setOpenMailId] = useState(null);
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const tabs = useMemo(
    () =>
      PRIMARY_TABS.filter((t) => {
        if (t.id === 'forms') return caps.view_client_faq;
        if (t.id === 'files-media') return caps.view_client_assets || caps.view_sharepoint || caps.view_portal_projects;
        if (t.id === 'communication') return caps.view_client_emails || caps.view_client_meetings;
        if (t.id === 'tasks') return caps.view_client_tasks;
        return true;
      }),
    [caps.view_client_faq, caps.view_client_assets, caps.view_sharepoint, caps.view_portal_projects, caps.view_client_emails, caps.view_client_meetings, caps.view_client_tasks],
  );
  const overflow = useMemo(
    () =>
      OVERFLOW_TABS.filter((t) => {
        if (t.id === 'tools') return caps.view_portal_projects;
        return true;
      }),
    [caps.view_portal_projects],
  );
  const [tab, setTab] = useState('overview');
  const [project, setProject] = useState(null);
  const [faq, setFaq] = useState(null);
  const [media, setMedia] = useState([]);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [contacts, setContacts] = useState(null);
  const [emails, setEmails] = useState(null);
  const [meetings, setMeetings] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [progressInputs, setProgressInputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [widgetRefresh, setWidgetRefresh] = useState(0);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && [...tabs, ...overflow].some((t) => t.id === tabParam)) {
      setTab(tabParam);
    }
  }, [searchParams, tabs, overflow]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiJson(`/api/my/projects/${id}`),
      caps.view_client_faq ? apiJson(`/api/my/projects/${id}/faq`).catch(() => null) : Promise.resolve(null),
      caps.view_client_assets ? apiJson(`/api/my/projects/${id}/media`).catch(() => []) : Promise.resolve([]),
      caps.view_client_assets ? apiJson('/api/my/assets/my-uploads').catch(() => []) : Promise.resolve([]),
      apiJson(`/api/my/projects/${id}/contacts`).catch(() => null),
      caps.view_client_emails ? apiJson(`/api/my/projects/${id}/emails`).catch(() => null) : Promise.resolve(null),
      caps.view_client_meetings ? apiJson(`/api/my/projects/${id}/meetings`).catch(() => null) : Promise.resolve(null),
      caps.view_client_tasks ? apiJson(`/api/my/tasks?project_id=${id}`).catch(() => []) : Promise.resolve([]),
    ])
      .then(([p, f, m, uploads, c, em, mt, t]) => {
        setProject(p);
        setProgressInputs(p.progress_inputs || []);
        setFaq(f);
        setMedia(m);
        setPendingUploads(
          (Array.isArray(uploads) ? uploads : []).filter((item) => String(item.project_id) === String(id)),
        );
        setContacts(c);
        setEmails(em);
        setMeetings(mt);
        setTasks(t);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, caps.view_client_faq, caps.view_client_assets, caps.view_client_emails, caps.view_client_meetings, caps.view_client_tasks, widgetRefresh]);

  const progressSummary = useMemo(
    () => summarizeTaskProgress(progressInputs.length ? progressInputs : tasks, project?.task_columns, { hideInternal: true }),
    [progressInputs, tasks, project],
  );

  const onTasksChange = (next) => {
    setTasks(next);
    setProgressInputs((inputs) =>
      inputs.map((row) => {
        const moved = next.find((task) => task.id === row.id);
        return moved ? { ...row, status: moved.status } : row;
      }),
    );
  };

  return (
    <div>
      {loading ? <p className="muted">Loading…</p> : null}
      {error ? <p className="err">{error}</p> : null}
      {project ? (
        <>
          <ProjectHero
            breadcrumb={<Link to="/my/projects">← All projects</Link>}
            name={project.name}
            subtitle={project.type ? String(project.type).replace(/[_-]/g, ' ') : undefined}
            status={project.status}
            statusLabel={projectHealth(project).label}
            statusTone={projectHealth(project).tone}
            progress={progressSummary.percent}
            meta={[
              { label: 'Current stage', value: progressSummary.current_stage || projectHealth(project).detail },
              { label: 'Target completion', value: project.due_date ? formatDate(project.due_date) : 'TBD' },
            ]}
            actions={
              project.site_url ? (
                <a className="btn-secondary" href={project.site_url} target="_blank" rel="noreferrer">
                  Open website
                </a>
              ) : null
            }
          />
          <div data-tour="project-tabs">
            <WorkspaceTabs tabs={tabs} overflow={overflow} active={tab} onChange={setTab} />
          </div>
          {tab === 'overview' ? (
            <ProjectOverviewBoard
              variant="client"
              project={project}
              tasks={tasks}
              summary={progressSummary}
              activity={project.status_events || []}
              meetings={meetings?.items || []}
              notes={project.notes}
              clientName={contacts?.client?.name}
              siteUrl={project.site_url}
              dueDate={project.due_date}
              onOpenTab={setTab}
            />
          ) : (
          <div className="panel" style={{ marginTop: '1rem' }}>
            {tab === 'notes' ? (
              project.notes ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>{project.notes}</p>
              ) : (
                <p className="muted">No project notes yet.</p>
              )
            ) : null}
            {tab === 'forms' ? (
              faq?.categories?.length ? (
                <ClientFaqViewer categories={faq.categories} plan={faq.plan} projectId={id} />
              ) : (
                <p className="muted">No project forms published yet.</p>
              )
            ) : null}
            {tab === 'files-media' ? (
              <>
                {caps.view_client_assets ? (
                  <>
                    <div className="media-library-head">
                      <div>
                        <h3 className="panel-title">Files & Media</h3>
                        <p className="muted small">Files your agency has shared with you. Uploads stay hidden until they are approved.</p>
                      </div>
                    </div>
                    <ProjectFileUploader
                      compact
                      endpoint="/api/my/assets/upload"
                      fields={{ project_id: id }}
                      description="or drag files here to send them to your agency"
                      onUploaded={() => {
                        apiJson(`/api/my/projects/${id}/media`).then(setMedia).catch(() => {});
                        apiJson('/api/my/assets/my-uploads')
                          .then((uploads) =>
                            setPendingUploads(
                              (Array.isArray(uploads) ? uploads : []).filter((item) => String(item.project_id) === String(id)),
                            ),
                          )
                          .catch(() => {});
                      }}
                    />
                    <ProjectMediaLibrary
                      items={media}
                      projectId={id}
                      mode="client"
                      pending={pendingUploads}
                      onRefresh={() => apiJson(`/api/my/projects/${id}/media`).then(setMedia).catch(() => {})}
                    />
                  </>
                ) : null}
                {caps.view_sharepoint || caps.view_portal_projects ? (
                  <>
                    <h3 className="panel-title" style={{ marginTop: '1.25rem' }}>SharePoint</h3>
                    <SharePointFileBrowser
                      apiBase={`/api/my/projects/${id}/sharepoint-files`}
                      openInBrowserUrl={project.teams_files_web_url}
                    />
                    {project.documents?.length ? (
                      <>
                        <h3>Shared documents</h3>
                        <ul>
                          {project.documents.map((d) => (
                            <li key={d.id}>
                              <a href={d.url} target="_blank" rel="noreferrer">
                                {d.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}
            {tab === 'tools' ? (
              <>
                {caps.view_integration_widgets ? (
                  <>
                    <IntegrationWidgetsPanel widgets={project.integration_widgets} />
                    {caps.figma && project.integration_widgets?.figma?.pending_approval ? (
                      <FigmaApprovalClientPanel
                        projectId={id}
                        approval={project.integration_widgets.figma.pending_approval}
                        onUpdated={() => setWidgetRefresh((n) => n + 1)}
                      />
                    ) : null}
                  </>
                ) : null}
                {caps.view_integration_widgets && project.integration_tool_links?.length ? (
                  <>
                    <h3>Integrations</h3>
                    <ul>
                      {project.integration_tool_links.map((l) => (
                        <li key={l.key}>
                          <a href={l.url} target="_blank" rel="noreferrer">
                            {l.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
                <h3>Quick links</h3>
                {Array.isArray(project.quick_links) && project.quick_links.length ? (
                  <ul>
                    {project.quick_links.map((l, i) => (
                      <li key={i}>
                        <a href={l.url} target="_blank" rel="noreferrer">
                          {l.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">No quick links yet.</p>
                )}
              </>
            ) : null}
            {tab === 'contacts' ? (
              <>
                {contacts?.agency ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <h3>Your agency</h3>
                    <p className="muted small" style={{ margin: 0 }}>
                      <strong>{contacts.agency.name}</strong>
                    </p>
                    {contacts.agency.support_email ? (
                      <p className="muted small" style={{ margin: 0 }}>
                        <a href={`mailto:${contacts.agency.support_email}`}>{contacts.agency.support_email}</a>
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {contacts?.client ? (
                  <div>
                    <h3>Your company</h3>
                    <p className="muted small" style={{ margin: 0 }}>
                      <strong>{contacts.client.name}</strong>
                    </p>
                    {contacts.client.email ? (
                      <p className="muted small" style={{ margin: 0 }}>{contacts.client.email}</p>
                    ) : null}
                    {contacts.client.phone ? (
                      <p className="muted small" style={{ margin: 0 }}>{contacts.client.phone}</p>
                    ) : null}
                    {contacts.client.address ? (
                      <p className="muted small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{contacts.client.address}</p>
                    ) : null}
                  </div>
                ) : (
                  <p className="muted">No contact details available.</p>
                )}
              </>
            ) : null}
            {tab === 'communication' ? (
              <>
                {caps.view_client_emails ? <MailboxEmailsList data={emails} loading={loading} onOpen={(email) => setOpenMailId(email.id)} /> : null}
                <MailMessageDialog
                  apiBase="/api/my/emails"
                  messageId={openMailId}
                  onClose={() => setOpenMailId(null)}
                />
                {caps.view_client_meetings ? <MailboxMeetingsList data={meetings} loading={loading} /> : null}
              </>
            ) : null}
            {tab === 'tasks' ? (
              <>
                <TaskBoard mode="client" tasks={tasks} onChange={onTasksChange} columns={project.task_columns} onError={setError} />
                <Link to="/my/tasks" className="small">
                  All my tasks →
                </Link>
              </>
            ) : null}
            {tab === 'activity' ? (
              <ul className="timeline">
                {(project.status_events || []).map((ev) => (
                  <li key={ev.id}>
                    <strong>{ev.title}</strong>
                    <span className="muted small"> · {ev.source}</span>
                    {ev.body ? <div className="muted small">{ev.body}</div> : null}
                    <div className="muted small">{formatDateTime(ev.created_at)}</div>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          )}
        </>
      ) : null}
    </div>
  );
}
